/**
 * consegneService — la console consegna gli strumenti che hanno bisogno di noi (atlante cap. 264, «L'amministratore
 * attiva il centralino da solo»).
 *
 * L'amministratore di un'azienda preme «Attiva» sul telefono: si apre una RICHIESTA, e il canone non parte. A noi
 * restano la pratica del gestore coi documenti dell'azienda e la richiesta del numero; quando il numero arriva, un clic
 * qui («Assegna il numero all'azienda») fa il resto e consegna il centralino: il canone parte quel giorno.
 *
 * Due porte, tutte e due del server e solo per il super utente:
 *  - `consoleStrumentiInAttesaDiConsegna`: chi aspetta, da quando, e cosa succederebbe consegnando oggi;
 *  - `consoleAssegnaNumeroTelefono`: sottoaccount Twilio, fascicolo, numero spostato, voce puntata, registro, poi la
 *    consegna. Idempotente: rilanciata sullo stesso numero salta i passi già fatti.
 * In più una lettura: i numeri già registrati per un'azienda (`centralino_numeri`, che il super utente legge per regola).
 *
 * Logging: ogni chiamata registra in console nome, durata ed esito.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { ViaDiAttivazione } from '@shared/strumenti/attivazione';

/**
 * Lo strumento che si consegna assegnando un numero: il telefono (la capability del centralino, una chiave che non si
 * rinomina). Oggi è l'unico strumento del listino con `richiede_consegna`; se ne arriverà un altro, avrà la sua
 * operazione di consegna accanto a questa.
 */
export const STRUMENTO_TELEFONO = 'centralino_ai';

/** Cosa succederebbe consegnando oggi: la stessa regola della consegna (`viaDiAttivazione`), date in ISO. */
export interface AllaConsegna {
  via: ViaDiAttivazione['via'];
  gratis_fino_al: string | null;
}

/** Una richiesta che aspetta la consegna (`consoleStrumentiInAttesaDiConsegna`). */
export interface RichiestaInAttesa {
  tenant_id: string;
  nome_azienda: string;
  strumento: string;
  /** Il nome dello strumento nel listino. */
  etichetta: string;
  richiesta_il: string | null;
  richiesta_da_uid: string | null;
  richiesta_da_nome: string | null;
  richiesta_via: 'mano' | 'assistente_ai';
  alla_consegna: AllaConsegna;
}

export const PASSI_ASSEGNAZIONE = ['sottoaccount', 'fascicolo', 'numero', 'voce', 'registro'] as const;
export type PassoAssegnazione = (typeof PASSI_ASSEGNAZIONE)[number];
export type EsitoDelPasso = 'fatto' | 'gia_fatto' | 'non_serve';

export interface PassoEseguito {
  passo: PassoAssegnazione;
  esito: EsitoDelPasso;
  /** In italiano, scritta dal server per chi legge la console. */
  descrizione: string;
  sid: string | null;
}

export type ConsegnaDelCentralino =
  | { esito: 'consegnato'; via: string; gratis_fino_al: string | null; richiesta_chiusa: boolean }
  | { esito: 'gia_consegnato'; per: 'gia_acceso' | 'piano' | 'regalo'; richiesta_chiusa: boolean };

/** Il risultato di «Assegna il numero all'azienda» (specchio di `RisultatoAssegnazioneEConsegna` del server). */
export interface RisultatoAssegnazione {
  tenant_id: string;
  /** Il numero in E.164, normalizzato dal server. */
  numero: string;
  sottoaccount_sid: string;
  numero_sid: string;
  fascicolo_sid: string | null;
  indirizzo_voce: string;
  passi: PassoEseguito[];
  gia_tutto_fatto: boolean;
  riepilogo: string;
  consegna: ConsegnaDelCentralino;
}

async function chiama<I, O>(nome: string, dati: I): Promise<O> {
  const inizio = performance.now();
  try {
    const r = await httpsCallable<I, O>(functions, nome)(dati);
    console.info(`[consegne] ${nome} ok in ${Math.round(performance.now() - inizio)} ms`);
    return r.data;
  } catch (err) {
    console.error(`[consegne] ${nome} fallita dopo ${Math.round(performance.now() - inizio)} ms`, err);
    throw err;
  }
}

export async function leggiRichiesteInAttesa(): Promise<RichiestaInAttesa[]> {
  const r = await chiama<Record<string, never>, { richieste?: RichiestaInAttesa[] }>('consoleStrumentiInAttesaDiConsegna', {});
  return Array.isArray(r?.richieste) ? r.richieste : [];
}

export async function assegnaNumeroAllAzienda(r: { tenant_id: string; numero: string }): Promise<RisultatoAssegnazione> {
  return chiama<typeof r, RisultatoAssegnazione>('consoleAssegnaNumeroTelefono', r);
}

/** I numeri veri che rispondono per un'azienda (quelli del banco di prova esclusi), in ordine. */
export async function leggiNumeriDellAzienda(tenantId: string): Promise<string[]> {
  const snap = await getDocs(query(collection(db, 'centralino_numeri'), where('tenant_id', '==', tenantId)));
  return snap.docs
    .filter((d) => d.data().attivo === true && d.data().prova !== true)
    .map((d) => d.id)
    .sort();
}

/** Un errore di una callable letto per la console: il messaggio del server e i suoi `details`. */
export interface ErroreDellaCallable {
  messaggio: string;
  codice: string | null;
  dettagli: Record<string, unknown>;
}

export function leggiErrore(err: unknown): ErroreDellaCallable {
  const e = (err ?? {}) as { message?: unknown; code?: unknown; details?: unknown };
  const dettagli = e.details && typeof e.details === 'object' && !Array.isArray(e.details) ? (e.details as Record<string, unknown>) : {};
  return {
    messaggio: typeof e.message === 'string' ? e.message : String(err),
    codice: typeof e.code === 'string' ? e.code.replace(/^functions\//, '') : null,
    dettagli,
  };
}
